import { useMemo } from 'react';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDiscoveryAppliedModels } from '@/hooks/discovery/appliedModels';
import { useDiscoveryDefinitionSemanticModels } from '@/hooks/discovery/definitionSemanticModels';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { ErdColumn, ErdModel, ErdRelationship, ErdSemanticModel } from '../types';
import { useErdModelColumns } from './useErdModelColumns';
import { ErdTestNode, useErdTests } from './useErdTests';

function transformModels(
  rawModels: ReturnType<typeof useDiscoveryAppliedModels>['data'],
  columnsByModelId: Map<string, ErdColumn[]>,
): ErdModel[] {
  if (!rawModels) return [];
  return rawModels.map((m) => ({
    uniqueId: m.uniqueId,
    name: m.name,
    schema: m.schema ?? '',
    database: m.database ?? '',
    description: '',
    columns: columnsByModelId.get(m.uniqueId) ?? [],
    owner: '',
    tags: (m.tags ?? []) as string[],
    materializedType: '',
    tests: [],
  }));
}

function extractRelationshipTestEdges(
  tests: ErdTestNode[],
  modelMap: Map<string, ErdModel>,
): ErdRelationship[] {
  const edges: ErdRelationship[] = [];

  const modelsByName = new Map<string, string>();
  for (const [uniqueId, model] of modelMap) {
    modelsByName.set(model.name.toLowerCase(), uniqueId);
  }

  for (const test of tests) {
    if (test.testMetadata?.name !== 'relationships') continue;

    const kwargs = test.testMetadata.kwargs ?? {};

    // Extract source and target from the `parents` field (most reliable).
    // Relationship tests have exactly 2 model parents: source and target.
    // kwargs.model contains a ref() to the source model (the model the test
    // is defined on), so match it against parents to identify source vs target.
    const modelParents = test.parents
      .map((p) => p.uniqueId)
      .filter((id) => id.startsWith('model.'));

    if (modelParents.length < 2) continue;

    // kwargs.model is a Jinja expression like {{ get_where_subquery(ref('source_model')) }}
    // that references the source model (the model the test is defined on).
    const kwargsModel = kwargs.model as string | undefined;
    const kwargsModelResolved = kwargsModel
      ? resolveRef(kwargsModel, modelsByName)
      : null;

    // kwargs.to (when present) references the target model
    const kwargsTo = kwargs.to as string | undefined;
    const kwargsToResolved = kwargsTo ? resolveRef(kwargsTo, modelsByName) : null;

    let sourceModelId: string | null = null;
    let targetModelId: string | null = null;

    if (kwargsModelResolved && kwargsToResolved) {
      // Both kwargs.model and kwargs.to are available — use them directly
      sourceModelId = kwargsModelResolved;
      targetModelId = kwargsToResolved;
    } else if (kwargsModelResolved) {
      // Only kwargs.model available — it's the source, the other parent is target
      sourceModelId = kwargsModelResolved;
      targetModelId = modelParents.find((id) => id !== kwargsModelResolved) ?? null;
    } else if (kwargsToResolved) {
      // Only kwargs.to available — it's the target, the other parent is source
      targetModelId = kwargsToResolved;
      sourceModelId = modelParents.find((id) => id !== kwargsToResolved) ?? null;
    } else {
      // Neither resolved — use parents[0] as source, parents[1] as target
      sourceModelId = modelParents[0];
      targetModelId = modelParents[1];
    }

    if (!sourceModelId || !targetModelId) continue;
    if (!modelMap.has(sourceModelId) || !modelMap.has(targetModelId)) continue;

    const field = (kwargs.field as string) ?? (kwargs.column_name as string);

    edges.push({
      id: `rel-test-${test.uniqueId}`,
      sourceModelId,
      targetModelId,
      sourceColumn: (kwargs.column_name as string) ?? '',
      targetColumn: field ?? '',
      label: (kwargs.column_name as string) ?? field ?? '',
      edgeSource: 'relationshipTest',
    });
  }

  return edges;
}

function resolveRef(ref: string, modelsByName: Map<string, string>): string | null {
  // Handle ref('model_name') syntax
  const refMatch = ref.match(/ref\(\s*'([^']+)'\s*\)/);
  if (refMatch?.[1]) {
    const modelName = refMatch[1].toLowerCase();
    return modelsByName.get(modelName) ?? null;
  }

  // Handle source('source_name', 'table_name') syntax
  const sourceMatch = ref.match(/source\(\s*'([^']+)'\s*,\s*'([^']+)'\s*\)/);
  if (sourceMatch?.[1] && sourceMatch[2]) {
    return `source.${sourceMatch[1]}.${sourceMatch[2]}`;
  }

  // Already a fully qualified unique ID
  if (ref.startsWith('model.') || ref.startsWith('source.')) {
    return ref;
  }

  // Fallback: try looking up as a model name
  return modelsByName.get(ref.toLowerCase()) ?? null;
}

function extractSemanticLayerEdges(
  semanticModels: ReturnType<typeof useDiscoveryDefinitionSemanticModels>['data'],
  modelMap: Map<string, ErdModel>,
): { edges: ErdRelationship[]; semanticModels: ErdSemanticModel[] } {
  if (!semanticModels) return { edges: [], semanticModels: [] };

  const erdSemanticModels: ErdSemanticModel[] = semanticModels.map((sm) => ({
    uniqueId: sm.uniqueId,
    name: sm.uniqueId.split('.').pop() ?? sm.uniqueId,
    description: sm.description ?? '',
    entities: (sm.entities ?? []).map(
      (e: { name: string | null; type: string | null }) => ({
        name: e.name ?? '',
        type: e.type ?? 'unknown',
        description: '',
      }),
    ),
    parentModelUniqueId: null,
  }));

  const entityToPrimaryModel = new Map<string, string>();
  const entityToForeignModels = new Map<string, string[]>();

  for (const sm of semanticModels) {
    if (!sm.entities) continue;
    for (const entity of sm.entities) {
      if (!entity.name) continue;
      const smName = sm.uniqueId.split('.').pop() ?? sm.uniqueId;
      const matchingModel = findMatchingModel(smName, modelMap);
      if (!matchingModel) continue;

      const entityType = entity.type;
      if (entityType === 'primary' || entityType === 'natural') {
        entityToPrimaryModel.set(entity.name, matchingModel);
      } else if (entityType === 'foreign') {
        const existing = entityToForeignModels.get(entity.name) ?? [];
        existing.push(matchingModel);
        entityToForeignModels.set(entity.name, existing);
      }
    }
  }

  const edges: ErdRelationship[] = [];
  for (const [entityName, primaryModelId] of entityToPrimaryModel) {
    const foreignModels = entityToForeignModels.get(entityName) ?? [];
    for (const foreignModelId of foreignModels) {
      if (foreignModelId === primaryModelId) continue;
      edges.push({
        id: `sl-${entityName}-${primaryModelId}-${foreignModelId}`,
        sourceModelId: foreignModelId,
        targetModelId: primaryModelId,
        sourceColumn: entityName,
        targetColumn: entityName,
        label: entityName,
        edgeSource: 'semanticLayer',
      });
    }
  }

  return { edges, semanticModels: erdSemanticModels };
}

function findMatchingModel(
  semanticModelName: string,
  modelMap: Map<string, ErdModel>,
): string | null {
  for (const [uniqueId, model] of modelMap) {
    if (model.name === semanticModelName) return uniqueId;
  }
  for (const [uniqueId] of modelMap) {
    const modelName = uniqueId.split('.').pop();
    if (modelName === semanticModelName) return uniqueId;
  }
  return null;
}

export function useErdData() {
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();

  const resolvedUserId = userId === 'loading' ? null : userId;

  const modelsQuery = useDiscoveryAppliedModels({
    first: 500,
    environmentId,
    userId: resolvedUserId,
    includeQueryCount: false,
  });

  const {
    tests: erdTests,
    fetching: testsFetching,
    error: testsError,
  } = useErdTests({ environmentId, userId: resolvedUserId });

  const semanticModelsQuery = useDiscoveryDefinitionSemanticModels({
    first: 500,
    environmentId,
    userId: resolvedUserId,
  });

  const {
    columnsByModelId,
    fetching: columnsFetching,
    error: columnsError,
  } = useErdModelColumns({
    environmentId,
    userId: resolvedUserId,
  });

  const models = useMemo(
    () => transformModels(modelsQuery.data, columnsByModelId),
    [modelsQuery.data, columnsByModelId],
  );

  const modelMap = useMemo(() => {
    const map = new Map<string, ErdModel>();
    for (const model of models) {
      map.set(model.uniqueId, model);
    }
    return map;
  }, [models]);

  const relationshipTestEdges = useMemo(
    () => extractRelationshipTestEdges(erdTests, modelMap),
    [erdTests, modelMap],
  );

  const { edges: semanticLayerEdges, semanticModels: erdSemanticModels } = useMemo(
    () => extractSemanticLayerEdges(semanticModelsQuery.data, modelMap),
    [semanticModelsQuery.data, modelMap],
  );

  const fetching =
    modelsQuery.fetching ||
    testsFetching ||
    semanticModelsQuery.fetching ||
    columnsFetching;
  const error =
    modelsQuery.error || testsError || semanticModelsQuery.error || columnsError;

  return {
    models,
    modelMap,
    relationshipTestEdges,
    semanticLayerEdges,
    semanticModels: erdSemanticModels,
    fetching,
    error,
  };
}
